import React, { useState } from 'react'
import { Button, Table } from 'semantic-ui-react'
import styled from 'styled-components'
import { formatMoney, formatShortDate, relativeDays } from 'utility/crm/dates'
import { HEALTH_FACTORS, HEALTH_LEGEND, bandTone, factorPercent, factorTone } from 'utility/crm/health'
import { COMPLIANCE_TARGET, isBelowTarget, orderInspections, sparkline, trendPoints } from 'utility/crm/inspections'
import { useAccountActivities } from 'hooks/crm'
import { AsyncState, Avatar, Card, ChipButton, Muted, Pill, Row, StagePill } from './ui'

const Bar = styled.div`
    height: 8px;
    border-radius: 4px;
    background: rgba(128, 128, 128, 0.25);
    overflow: hidden;
    > div {
        height: 100%;
        border-radius: 4px;
        background: ${({ $tone }) => ({ green: '#16a34a', amber: '#d97706', red: '#dc2626', gray: '#6b7280' }[$tone])};
    }
`

const BigScore = styled.div`
    font-size: 2.4rem;
    font-weight: 800;
    line-height: 1;
`

/** Health tab: score, label, reasons and the four factor bars. */
export const HealthTab = ({ health }) => {
    if (!health) return <Muted>No health data for this account yet.</Muted>
    const tone = bandTone(health.Band)
    return (
        <div>
            <Row $gap={14} style={{ marginBottom: 14 }}>
                <BigScore aria-label={`Health score ${health.Score} out of 100`}>{health.Score}</BigScore>
                <Pill $tone={tone}>{health.Label}</Pill>
            </Row>
            <h4 style={{ margin: '0 0 4px' }}>Why</h4>
            {health.Reasons?.length ? (
                <ul style={{ margin: '0 0 16px', paddingLeft: 18 }}>
                    {health.Reasons.map(reason => (
                        <li key={reason}>{reason}</li>
                    ))}
                </ul>
            ) : (
                <Muted>No risk factors flagged.</Muted>
            )}
            {HEALTH_FACTORS.map(factor => {
                const value = health[factor.key] ?? 0
                const percent = factorPercent(value, factor.max)
                return (
                    <div key={factor.key} style={{ marginBottom: 12 }}>
                        <Row $between>
                            <span>{factor.label}</span>
                            <strong>
                                {value} / {factor.max}
                            </strong>
                        </Row>
                        <Bar $tone={factorTone(percent)} role="progressbar" aria-label={factor.label} aria-valuemin={0} aria-valuemax={factor.max} aria-valuenow={value}>
                            <div style={{ width: `${percent}%` }} />
                        </Bar>
                    </div>
                )
            })}
            <Muted style={{ display: 'block', marginTop: 8 }}>{HEALTH_LEGEND}</Muted>
        </div>
    )
}

const SPARK_W = 300
const SPARK_H = 80

/** Compliance trend from completed, scored inspections, with a dashed line at the 80% target. */
export const Sparkline = ({ inspections }) => {
    const points = trendPoints(inspections)
    if (points.length === 0) return <Muted>No scored inspections yet.</Muted>
    const geo = sparkline(points, { width: SPARK_W, height: SPARK_H })
    const first = points[0]
    const last = points[points.length - 1]
    return (
        <div>
            <svg
                viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                width="100%"
                height="90"
                preserveAspectRatio="none"
                role="img"
                aria-label={`Compliance trend: ${points.length} inspection${points.length === 1 ? '' : 's'}, from ${first.score}% on ${formatShortDate(first.date)} to ${last.score}% on ${formatShortDate(last.date)}. Target ${COMPLIANCE_TARGET}%.`}
            >
                <line x1="0" x2={SPARK_W} y1={geo.targetY} y2={geo.targetY} stroke="#9ca3af" strokeDasharray="4 4" />
                {points.length > 1 ? <path d={geo.path} fill="none" stroke="#2563eb" strokeWidth="2" vectorEffect="non-scaling-stroke" /> : null}
                {geo.dots.map(dot => (
                    <circle key={`${dot.date}-${dot.score}`} cx={dot.x} cy={dot.y} r="3.5" fill={isBelowTarget(dot.score) ? '#dc2626' : '#16a34a'} vectorEffect="non-scaling-stroke">
                        <title>{`${dot.score}% on ${formatShortDate(dot.date)}`}</title>
                    </circle>
                ))}
            </svg>
            <Row $between>
                <Muted>{formatShortDate(first.date)}</Muted>
                <Muted>dashed line = {COMPLIANCE_TARGET}% target</Muted>
                <Muted>{formatShortDate(last.date)}</Muted>
            </Row>
        </div>
    )
}

/** Audits & tickets tab. Empty until the Won handoff links the account to an eAuditor customer. */
export const AuditsTicketsTab = ({ inspections, tickets, isCustomer }) => {
    const ordered = orderInspections(inspections)
    return (
        <div>
            <h4 style={{ margin: '0 0 6px' }}>Compliance trend</h4>
            <Sparkline inspections={inspections} />

            <h4 style={{ margin: '16px 0 6px' }}>Inspections</h4>
            {ordered.length === 0 ? (
                <Muted>{isCustomer ? 'No inspections yet.' : 'Inspections appear here once the deal is won and the customer is set up.'}</Muted>
            ) : (
                <Table compact="very" aria-label="Inspections">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Date</Table.HeaderCell>
                            <Table.HeaderCell>Template</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right">Score</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {ordered.map(i => (
                            <Table.Row key={i.AuditId}>
                                <Table.Cell>{formatShortDate(i.Date)}</Table.Cell>
                                <Table.Cell>{i.TemplateName}</Table.Cell>
                                <Table.Cell textAlign="right">
                                    {i.Status === 'Completed' && typeof i.Score === 'number' ? (
                                        <strong style={isBelowTarget(i.Score) ? { color: '#dc2626' } : undefined}>{i.Score}%</strong>
                                    ) : (
                                        <Pill $tone={i.Status === 'Scheduled' ? 'blue' : 'amber'}>{i.Status}</Pill>
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}

            <h4 style={{ margin: '16px 0 6px' }}>Auto-tickets</h4>
            {(tickets || []).length === 0 ? (
                <Muted>No tickets.</Muted>
            ) : (
                (tickets || []).map(t => (
                    <Row key={t.TicketId} $between $gap={8} style={{ padding: '6px 0', borderBottom: '1px solid rgba(128,128,128,.2)' }}>
                        <span>{t.Title}</span>
                        {t.IsOpen ? (
                            <Pill $tone={t.IsOverdue ? 'red' : 'gray'}>{t.DueOn ? `Due ${formatShortDate(t.DueOn)}` : 'Open'}</Pill>
                        ) : (
                            <Pill $tone="green">Resolved</Pill>
                        )}
                    </Row>
                ))
            )}
        </div>
    )
}

const TYPE_LABEL = { note: 'Note', call: 'Call', email: 'Email', meeting: 'Meeting', stage: 'Stage change', task: 'Task', system: 'System' }

const Entry = styled.div`
    padding: 10px 0;
    border-bottom: 1px solid rgba(128, 128, 128, 0.25);
    .head {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        font-weight: 600;
    }
    .body {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        margin: 4px 0;
    }
`

/** Timeline tab with a type filter (client side, only types that exist). */
export const AccountTimeline = ({ accountId }) => {
    const activities = useAccountActivities(accountId)
    const [filter, setFilter] = useState('all')
    const all = activities.data || []
    const types = Array.from(new Set(all.map(a => a.Type)))
    const shown = filter === 'all' ? all : all.filter(a => a.Type === filter)

    return (
        <div>
            <Row $gap={6} $wrap role="group" aria-label="Filter timeline" style={{ marginBottom: 6 }}>
                <ChipButton type="button" $active={filter === 'all'} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
                    All
                </ChipButton>
                {types.map(type => (
                    <ChipButton key={type} type="button" $active={filter === type} aria-pressed={filter === type} onClick={() => setFilter(type)}>
                        {TYPE_LABEL[type] || type}
                    </ChipButton>
                ))}
            </Row>
            <AsyncState loading={activities.loading} error={activities.error} hasData={Boolean(activities.data)} empty={Boolean(activities.data) && shown.length === 0} emptyText="No activity yet." onRetry={activities.refetch}>
                {shown.map(a => (
                    <Entry key={a.ActivityId}>
                        <div className="head">
                            <span>{a.Title}</span>
                            <Muted>
                                {formatShortDate(a.Date)} · {relativeDays(a.Date)}
                            </Muted>
                        </div>
                        {a.Body ? <div className="body">{a.Body}</div> : null}
                        <Muted>{[TYPE_LABEL[a.Type] || a.Type, a.ContactName, a.OwnerName].filter(Boolean).join(' · ')}</Muted>
                    </Entry>
                ))}
            </AsyncState>
        </div>
    )
}

/** Deals tab. */
export const AccountDeals = ({ deals, onOpenDeal, onNewDeal }) => (
    <div>
        <Row $between style={{ marginBottom: 8 }}>
            <h4 style={{ margin: 0 }}>Deals</h4>
            <Button type="button" size="mini" primary onClick={onNewDeal}>
                New deal
            </Button>
        </Row>
        {(deals || []).length === 0 ? <Muted>No deals for this account.</Muted> : null}
        {(deals || []).map(deal => (
            <Card
                key={deal.DealId}
                role="button"
                tabIndex={0}
                aria-label={`Open deal ${deal.Name}`}
                onClick={() => onOpenDeal(deal.DealId)}
                onKeyDown={event => {
                    if (event.key === 'Enter') onOpenDeal(deal.DealId)
                }}
                style={{ cursor: 'pointer', marginBottom: 8 }}
            >
                <Row $between $wrap>
                    <strong>{deal.Name}</strong>
                    <span>{formatMoney(deal.Amount)}</span>
                </Row>
                <Row $gap={8} $wrap>
                    <StagePill stage={deal.Status === 'lost' ? 'Lost' : deal.Stage} />
                    <Muted>
                        {[deal.Pipeline, deal.ContactName, deal.CloseDate ? `close ${formatShortDate(deal.CloseDate)}` : null].filter(Boolean).join(' · ')}
                    </Muted>
                </Row>
            </Card>
        ))}
    </div>
)

/** Contacts tab. */
export const AccountContacts = ({ contacts, onOpenContact }) => (
    <div>
        {(contacts || []).length === 0 ? <Muted>No contacts at this account.</Muted> : null}
        {(contacts || []).map(c => (
            <Row key={c.ContactId} $between $gap={8} style={{ padding: '8px 0', borderBottom: '1px solid rgba(128,128,128,.2)' }}>
                <Row $gap={10}>
                    <Avatar name={c.Name} />
                    <div>
                        <strong>{c.Name}</strong>
                        <div>
                            <Muted>{c.Title}</Muted>
                        </div>
                    </div>
                </Row>
                <Button type="button" size="mini" onClick={() => onOpenContact(c.ContactId)} aria-label={`Open ${c.Name}`}>
                    Open
                </Button>
            </Row>
        ))}
    </div>
)
